/**
 * Tabbed content container.
 *
 * Sits below the toolbar/status bar. Apps add content panels as tabs.
 * If only one tab exists, the tab header bar is hidden.
 *
 * Usage:
 *   const tabs = new TabView(document.getElementById('contentArea'));
 *   tabs.addTab('dashboard', 'Dashboard', dashboardEl);
 *   tabs.addTab('settings', 'Settings', settingsEl);
 *   tabs.setActiveTab('dashboard');
 *
 *   // Later
 *   tabs.removeTab('settings');
 */
export class TabView {
    constructor(container, options = {}) {
        this._container = container;
        this._onTabChange = options.onTabChange || null;
        this._tabs = new Map();      // id → { label, contentEl, headerBtn }
        this._groups = new Map();    // groupId → { label, el }
        this._activeTabId = null;
        this._render();
    }

    /**
     * Create a named group in the tab bar. Tabs added with { groupId } attach into it.
     * Rendered as an inline fieldset with a small group label.
     */
    addTabGroup(groupId, label) {
        if (this._groups.has(groupId)) return;
        const el = document.createElement('div');
        el.className = 'nm-tab-group';
        el.dataset.groupId = groupId;
        if (label) {
            const lbl = document.createElement('span');
            lbl.className = 'nm-tab-group-label';
            lbl.textContent = label;
            el.appendChild(lbl);
        }
        this._tabBar.appendChild(el);
        this._groups.set(groupId, { label, el });
    }

    /**
     * Add a tab. The contentEl is moved into the tab panel area.
     * First tab added becomes active automatically.
     * @param {Object} [opts]
     * @param {string} [opts.groupId] - attach button into a previously created group
     * @param {string} [opts.className] - extra class applied to the tab button (e.g. for color-coding)
     * @param {string} [opts.title] - native tooltip shown on hover (expands abbreviated labels)
     */
    addTab(id, label, contentEl, opts = {}) {
        if (this._tabs.has(id)) return;

        // Create header button
        const btn = document.createElement('button');
        btn.className = 'nm-tab-btn';
        if (opts.className) btn.classList.add(opts.className);
        btn.textContent = label;
        if (opts.title) btn.title = opts.title;
        btn.addEventListener('click', () => this.setActiveTab(id));
        const host = opts.groupId && this._groups.get(opts.groupId)
            ? this._groups.get(opts.groupId).el
            : this._tabBar;
        host.appendChild(btn);

        // Wrap content in a panel div
        const panel = document.createElement('div');
        panel.className = 'nm-tab-panel';
        panel.style.display = 'none';
        panel.appendChild(contentEl);
        this._panelArea.appendChild(panel);

        this._tabs.set(id, { label, contentEl, headerBtn: btn, panel });
        this._updateTabBarVisibility();

        // Auto-activate first tab
        if (this._tabs.size === 1) {
            this.setActiveTab(id);
        }
    }

    /** Remove a tab and its content. Returns the contentEl for re-use. */
    removeTab(id) {
        const tab = this._tabs.get(id);
        if (!tab) return null;

        const contentEl = tab.contentEl;
        tab.headerBtn.remove();
        tab.panel.remove();
        this._tabs.delete(id);

        // If removed tab was active, switch to first remaining tab
        if (this._activeTabId === id) {
            this._activeTabId = null;
            const first = this._tabs.keys().next().value;
            if (first) this.setActiveTab(first);
        }

        this._updateTabBarVisibility();
        return contentEl;
    }

    /** Switch to a tab by id. */
    setActiveTab(id) {
        const tab = this._tabs.get(id);
        if (!tab) return;

        // Deactivate current
        if (this._activeTabId) {
            const prev = this._tabs.get(this._activeTabId);
            if (prev) {
                prev.headerBtn.classList.remove('nm-tab-active');
                prev.panel.style.display = 'none';
            }
        }

        // Activate new
        this._activeTabId = id;
        tab.headerBtn.classList.add('nm-tab-active');
        tab.panel.style.display = '';

        this._onTabChange?.(id);
    }

    /** Get the active tab id. */
    getActiveTab() {
        return this._activeTabId;
    }

    /** Rename a tab. */
    renameTab(id, label) {
        const tab = this._tabs.get(id);
        if (!tab) return;
        tab.label = label;
        tab.headerBtn.textContent = label;
    }

    _render() {
        this._wrapper = document.createElement('div');
        this._wrapper.className = 'nm-tab-view';

        // Tab header bar
        this._tabBar = document.createElement('div');
        this._tabBar.className = 'nm-tab-bar';
        this._tabBar.style.display = 'none';
        this._wrapper.appendChild(this._tabBar);

        // Panel area (holds all tab panels, only one visible at a time)
        this._panelArea = document.createElement('div');
        this._panelArea.className = 'nm-tab-panels';
        this._wrapper.appendChild(this._panelArea);

        this._container.appendChild(this._wrapper);
    }

    /** Show tab bar only when there are 2+ tabs. */
    _updateTabBarVisibility() {
        this._tabBar.style.display = this._tabs.size > 1 ? '' : 'none';
    }
}
